import api from './api';

const API_URL = '/notifications';

/**
 * Get notifications for the current user
 * @param {Object} options - Query options
 * @param {number} options.limit - Max notifications to fetch
 * @param {number} options.offset - Offset for pagination
 * @param {boolean} options.unreadOnly - Only fetch unread notifications
 * @returns {Promise<Object>} - Notifications data
 */
export const getNotifications = async ({ limit = 20, offset = 0, unreadOnly = false } = {}) => {
  try {
    const response = await api.get(API_URL, {
      params: { limit, offset, unreadOnly }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 * @returns {Promise<number>} - Unread count
 */
export const getUnreadCount = async () => {
  try {
    const response = await api.get(`${API_URL}/count`);
    return response.data.count;
  } catch (error) {
    console.error('Error fetching notification count:', error);
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} - Updated notification
 */
export const markAsRead = async (notificationId) => {
  try {
    const response = await api.patch(`${API_URL}/${notificationId}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} - Result
 */
export const markAllAsRead = async () => {
  try {
    const response = await api.patch(`${API_URL}/read-all`);
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} - Result
 */
export const deleteNotification = async (notificationId) => {
  try {
    const response = await api.delete(`${API_URL}/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Delete all notifications
 * @returns {Promise<Object>} - Result
 */
export const deleteAllNotifications = async () => {
  try {
    const response = await api.delete(API_URL);
    return response.data;
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    throw error;
  }
};

/**
 * Get notification icon based on type
 * @param {string} type - Notification type
 * @returns {string} - Icon name
 */
export const getNotificationIcon = (type) => {
  const iconMap = {
    analysis_complete: 'Analytics',
    analysis_failed: 'Error',
    crew_added: 'PersonAdd',
    crew_removed: 'PersonRemove',
    script_uploaded: 'Upload',
    script_version: 'Description',
    callsheet_saved: 'EventNote',
    project_update: 'Folder',
    invitation: 'Mail',
    system: 'Settings',
    info: 'Info'
  };
  return iconMap[type] || 'Notifications';
};

/**
 * Get notification color based on type
 * @param {string} type - Notification type
 * @returns {string} - Color name
 */
export const getNotificationColor = (type) => {
  const colorMap = {
    analysis_complete: 'success',
    analysis_failed: 'error',
    crew_added: 'info',
    crew_removed: 'warning',
    script_uploaded: 'primary',
    script_version: 'primary',
    callsheet_saved: 'success',
    project_update: 'info',
    invitation: 'secondary',
    system: 'default',
    info: 'info'
  };
  return colorMap[type] || 'default';
};

export default {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  deleteAllNotifications,
  getNotificationIcon,
  getNotificationColor
};
